import { checkForUpdates, fetchKioskFilters } from "@actions";
import { emptyKioskSection, setKiosk } from "@actions/kiosk";
import { showFloatLevelSettingsModal } from "@actions/modals";
import Kiosk from "@api/kiosk";
import MachineStatusFilters from "@components/LiveFloorViewer/MachineStatusFilters";
import Filters from "@components/kiosk/kioskFilters";
import { defaultGrid } from "@helpers/datagrid";
import { handleDownload } from "@helpers/downloads";
import { getControlValues } from "@helpers/summary";
import KioskModal from "@modals/KioskModal";
import DataGrid from "@shared/DataGrid";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import { Grid, Pagination } from "semantic-ui-react";

class KioskView extends React.Component {
  state = {
    positionsLoading: false,
    transactionsLoading: false,
    summaryLoading: false,
    exportLoading: false,
    expanded: false,
    showSummary: true,
    activeRow: { installationId: null },
    page: 1,
    statuses: {
      online: true,
      offlineSiteOpen: true,
      pending: true,
      authReq: true,
      offlineSiteClosed: true,
      onlineEFTDisabled: true,
      onlineNoFunds: true,
    },
    quantities: {
      onlineQty: 0,
      onlineEFTEnabledQty: 0,
      offlineSiteOpenQty: 0,
      pendingQty: 0,
      authReqQty: 0,
      offlineSiteClosedQty: 0,
      unknownQty: 0,
      onlineEFTDisabledQty: 0,
      onlineNoFundsQty: 0,
    },
  };

  componentDidUpdate(prevProps) {
    if (checkForUpdates(prevProps.filters, this.props.filters)) {
      this.handleRefresh();
    }
  }

  componentWillUnmount() {
    this.props.emptyKioskSection();
  }

  handleRefresh = () => {
    const site = this.props.filters.filterSelected.site.id;
    const { filters } = this.props.data;
    const operator = this.props.filters.filterSelected.operator;
    const company = this.props.filters.filterSelected.company.id;
    const subcompany = this.props.filters.filterSelected.subcompany.id;
    const { online, offlineSiteOpen, offlineSiteClosed, pending, authReq } =
      this.state.statuses;

    this.setState({ positionsLoading: true }, () => {
      Kiosk.Positions(
        operator,
        company || -1,
        subcompany || -1,
        site || -1,
        online,
        offlineSiteOpen,
        offlineSiteClosed,
        pending,
        authReq,
      ).then(
        (positions) => {
          const activeRow = positions.grid.data.find(
            (x) => x.installationId === this.state.activeRow.installationId,
          ) || { installationId: null };
          const collectionDate =
            positions.collectionDates.length > 0
              ? positions.collectionDates[0].id
              : null;
          this.props
            .setKiosk({
              positions,
              filters: { ...filters, collectionDate },
              details: defaultGrid,
            })
            .then(() => {
              const quantities = {
                ...this.state.quantities,
                // Spread only the items from the response that are already in the original quantities object, it's unnecessary, but it just keeps clean data.
                ...Object.fromEntries(
                  Object.entries(positions).filter(([key]) =>
                    Object.keys(this.state.quantities).includes(key),
                  ),
                ),
              };
              this.setState(
                { quantities, activeRow, positionsLoading: false },
                () => {
                  if (activeRow.installationId) {
                    this.onTransactionsRefresh();
                  } else {
                    this.props.setKiosk({
                      transactions: defaultGrid,
                      summary: defaultGrid,
                    });
                  }
                },
              );
            });
        },
        () => this.setState({ positionsLoading: false }),
      );
    });
  };

  onExportClick = () => {
    const {
      includeEvents,
      fromDate,
      page,
      currentDate,
      collectionDate,
      useCollectionWeek,
    } = this.props.data.filters;
    const toDate = currentDate ? moment() : this.props.data.filters.toDate;
    const { activeRow } = this.state;
    const details = getControlValues(this.props.data.summary.grid.data);

    this.setState({ exportLoading: true, toDate }, () => {
      Kiosk.Export(
        activeRow.installationId,
        fromDate,
        toDate,
        collectionDate,
        useCollectionWeek,
        includeEvents,
        details,
        page,
      ).then(
        (response) =>
          this.setState({ exportLoading: false }, () =>
            handleDownload(response),
          ),
        () => this.setState({ exportLoading: false }),
      );
    });
  };

  onTransactionsRefresh = () => {
    const {
      includeEvents,
      fromDate,
      page,
      currentDate,
      collectionDate,
      useCollectionWeek,
    } = this.props.data.filters;
    const toDate = currentDate ? moment() : this.props.data.filters.toDate;
    const { activeRow } = this.state;
    const details = getControlValues(this.props.data.summary.grid.data);

    this.setState({ transactionsLoading: true, toDate }, () => {
      Kiosk.Transactions(
        activeRow.installationId,
        fromDate,
        toDate,
        collectionDate,
        useCollectionWeek,
        includeEvents,
        details,
        page,
      ).then(
        ({ transactions, summary }) =>
          this.props
            .setKiosk({
              activeRow,
              transactions,
              summary,
              details: defaultGrid,
            })
            .then(() => this.setState({ transactionsLoading: false })),
        () => this.setState({ transactionsLoading: false }),
      );
    });
  };

  onPositionClick = (activeRow) =>
    this.setState({ activeRow }, () => this.onTransactionsRefresh());

  onTransactionClick = (activeRow) => {
    const { installationId } = this.props.data.activeRow;
    const { detailId, optionShowDetails } = activeRow;

    if (!optionShowDetails) {
      return;
    }

    this.setState({ detailsLoading: true }, () => {
      Kiosk.Detail(installationId, detailId).then(
        (details) =>
          this.props
            .setKiosk({ details })
            .then(() =>
              this.setState({ modalOpen: true, detailsLoading: false }),
            ),
        () => this.setState({ detailsLoading: false }),
      );
    });
  };

  handleFilterChange = async (data) => {
    this.props.setKiosk({ filters: { ...this.props.data.filters, ...data } });
  };

  handleOptionChange = (option) => {
    const { grid } = this.props.data.summary;
    const data = [...grid.data];

    data.forEach((row, rowIndex) => {
      row.cellData.forEach((cell, cellIndex) => {
        if (cell.data.code === option.code) {
          data[rowIndex].cellData[cellIndex].data = { ...option };
        }
      });
    });

    const summary = { ...this.props.data.summary, grid: { ...grid, data } };

    this.props.setKiosk({ ...this.props.data, summary });
  };

  onPageChange = (event, { activePage }) => {
    this.props.data.filters.page = activePage;
    this.handleFilterChange("page", activePage).then(() =>
      this.onTransactionsRefresh(),
    );
  };

  onSettingsClick = () => {
    const { installationId } = this.state.activeRow;

    Kiosk.Floats(installationId).then((result) => {
      this.props.showFloatLevelSettingsModal(result);
    });
  };

  handleStatusChange = (key) => {
    let statuses = { ...this.state.statuses };
    statuses[key] = !statuses[key];
    this.setState({ statuses }, () => {
      if (this.props.filters.hasSelection) this.handleRefresh();
    });
  };

  render() {
    const { details, positions, transactions, summary, filters } =
      this.props.data;
    const { showOrgTree } = this.props.filters;
    const { page } = filters;
    const {
      statuses,
      expanded,
      quantities,
      exportLoading,
      positionsLoading,
      transactionsLoading,
      summaryLoading,
      activeRow,
      modalOpen,
    } = this.state;
    //    const showSummary = activeRow && this.state.showSummary && summary.grid.data.length > 0;

    const showSummary = true;

    const tablet = 8;
    const computer = 6;
    const largeScreen = 6;
    const widescreen = 4;

    return (
      <>
        <Grid className="height-100 gs-flex-container-vertical">
          <Grid.Row>
            <Grid.Column>
              <MachineStatusFilters
                onExportClick={this.onExportClick}
                onSummaryClick={() =>
                  this.setState({ showSummary: !showSummary })
                }
                showSummary={showSummary}
                onStatusChange={this.handleStatusChange}
                onRefreshClick={this.handleRefresh}
                filters={filters}
                statuses={statuses}
                activeRow={activeRow}
                quantities={quantities}
                fetchFilter={this.props.fetchKioskFilters}
                showOrgTree={showOrgTree}
                disabled={positionsLoading || !this.props.filters.hasSelection}
                onSettingsClick={this.onSettingsClick}
                expanded={expanded}
                onExpandClick={() =>
                  this.setState({ expanded: !this.state.expanded })
                }
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className={expanded ? "flex-stretch" : ""}>
            <Grid.Column stretched={expanded}>
              <DataGrid
                loading={positionsLoading}
                headings={positions.grid.headerDef}
                activeRow={this.state.activeRow}
                data={positions.grid.data}
                rowDef={positions.grid.rowDef}
                onRowClick={this.onPositionClick}
                fullHeight
                style={!expanded ? { height: "120px" } : {}}
              />
            </Grid.Column>
          </Grid.Row>
          {!expanded && (
            <>
              <Grid.Row>
                <Grid.Column width={16}>
                  <Filters
                    handleChange={this.handleFilterChange}
                    exportLoading={exportLoading}
                    disabled={!activeRow.installationId}
                    onRefresh={(e) => this.onPageChange(e, { activePage: 1 })}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row className="flex-stretch">
                {showSummary && (
                  <Grid.Column
                    widescreen={widescreen}
                    largeScreen={largeScreen}
                    computer={computer}
                    tablet={tablet}
                    className="gs-flex-container-vertical"
                  >
                    <DataGrid
                      celled={false}
                      structured={false}
                      loading={summaryLoading}
                      headings={summary.grid.headerDef}
                      data={summary.grid.data}
                      rowDef={summary.grid.rowDef}
                      disableActiveRow
                      fullHeight
                      onOptionChange={this.handleOptionChange}
                    />
                  </Grid.Column>
                )}
                <Grid.Column
                  widescreen={showSummary ? 16 - widescreen : 16}
                  largeScreen={showSummary ? 16 - largeScreen : 16}
                  computer={showSummary ? 16 - computer : 16}
                  tablet={showSummary ? 16 - tablet : 16}
                  className="gs-flex-container-vertical"
                >
                  <DataGrid
                    loading={transactionsLoading}
                    headings={transactions.grid.headerDef}
                    data={transactions.grid.data}
                    rowDef={transactions.grid.rowDef}
                    onRowClick={this.onTransactionClick}
                    fullHeight
                  />
                </Grid.Column>
              </Grid.Row>
            </>
          )}
          <Grid.Row>
            <Grid.Column width={16}>
              <Pagination
                className="mt-1"
                size="mini"
                key="pagination"
                activePage={page}
                totalPages={transactions.pageCount}
                onPageChange={this.onPageChange}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <KioskModal
          details={details}
          open={modalOpen}
          onClose={() => this.setState({ modalOpen: false })}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    filters: state.kioskFilters,
    data: state.kiosk,
  };
};

export default connect(mapStateToProps, {
  showFloatLevelSettingsModal,
  fetchKioskFilters,
  setKiosk,
  emptyKioskSection,
})(KioskView);
